import { UnitLabels } from 'const/UnitLabels';
import { isNil } from 'lodash';
import { getI18n } from 'react-i18next';

export const pick = function (data, key) {
  const list = Array.isArray(data) ? data : [];
  return list.map((val) => {
    return typeof key === 'function' ? key(val) : val[key];
  }, []);
};

export const divideIfNotZero = (numerator, denominator) => {
  if (denominator === 0 || isNaN(denominator)) {
    return 0;
  } else {
    return numerator / denominator;
  }
};

export const calculateDeviation = (currentUsage, previousUsage) => {
  if (isNaN(currentUsage)) {
    currentUsage = 0;
  }
  if (isNaN(previousUsage)) {
    previousUsage = 0;
  }
  let value;
  if (previousUsage === 0 && currentUsage === 0) {
    value = 0;
  } else if (previousUsage === 0 && currentUsage > 0) {
    value = 100;
  } else {
    value = ((currentUsage - previousUsage) / previousUsage) * 100;
  }
  return {
    trend: value === 0 ? 'equal' : value > 0 ? 'up' : 'down',
    value: Math.abs(value).toFixed(2),
  };
};

export const roundCurrencyCentsToWholeNumber = (value) => {
  if (isNaN(value)) return value;
  return value / 100;
};

export const setNestedKey = (obj, path, value) => {
  if (!obj[path[0]]) {
    obj[path[0]] = {};
  }
  if (path.length === 1) {
    obj[path] = value;
    return;
  }
  return setNestedKey(obj[path[0]], path.slice(1), value);
};

const STORE_UNIT = Object.freeze({
  BYTES: 'BYTES',
  KB: 'KB',
  MB: 'MB',
  GB: 'GB',
  TB: 'TB',
  PB: 'PB',
});
const STORE_UNIT_LIST = Object.keys(STORE_UNIT);

export const getStoreUnitFromBytes = (bytes, unit = 'detect', decimals = 2) => {
  let i = unit in STORE_UNIT ? STORE_UNIT_LIST.indexOf(unit) : unit;

  if (bytes === 0) return { value: 0, unit: STORE_UNIT_LIST[i] ?? STORE_UNIT.KB };

  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;

  if (unit === 'detect') {
    i = Math.floor(Math.log(bytes) / Math.log(k));
  }

  const formattedNumber = getI18n().format(parseFloat((bytes / Math.pow(k, i)).toFixed(dm)), 'number', undefined);

  return {
    value: formattedNumber,
    unit: STORE_UNIT_LIST[i],
  };
};

export const getBytesFromStoreUnit = (value, unit = 'MB', decimals = 2) => {
  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  if (!(unit in STORE_UNIT)) {
    throw new Error('Cannot convert to bytes: No such unit type');
  }
  const i = STORE_UNIT_LIST.indexOf(unit);

  return parseFloat(value * Math.pow(k, i).toFixed(dm));
};

export const countNonEmptyFilters = (filters, initialVal) => {
  return Object.entries(filters).reduce((acc, [key, val]) => {
    if ((Array.isArray(val) && val.length === 0) || val === undefined || val === null || val === '') {
      return acc;
    }
    acc += 1;
    return acc;
  }, initialVal);
};

const replacePlaceholders = (template, getDataCallback) => {
  const data = getDataCallback();

  return template.replace(/{{(.*?)}}/g, (match, key) => {
    key = key.trim();
    return key in data ? data[key] : match;
  });
};

export const getFormattedPrice = (item, fieldName) => {
  const formatter = getI18n().services.formatter;
  const priceTemplate = '{{ price }}/{{ volumeUnit }}';
  const minUnitTemplate = '{{ minUnitPrice }}/{{ minUnitVolume }} {{ minUnitVolumeUnit }}';

  try {
    const defaultPrice = replacePlaceholders(priceTemplate, () => ({
      price: isNil(item.price) ? '-' : formatter.format(item.price, 'currency'),
      volumeUnit: UnitLabels[item.volumeUnit],
    }));

    let price;
    let overage;
    if (item.minUnitPriceBilling) {
      price = replacePlaceholders(minUnitTemplate, () => ({
        minUnitPrice: formatter.format(item.minUnitPrice, 'currency'),
        minUnitVolume: item.minUnitVolume,
        minUnitVolumeUnit: UnitLabels[item.minUnitVolumeUnit],
      }));
      overage = defaultPrice;
    } else {
      price = defaultPrice;
      overage = '-';
    }

    if (typeof fieldName === 'function') {
      return fieldName(item, { price, overage, formatter });
    }

    return { price, overage }[fieldName];
  } catch (error) {
    return '-';
  }
};

export const downloadFile = (url) => {
  const tempLink = document.createElement('a');
  tempLink.href = url;
  tempLink.download = ''; // Let browser use default filename or Content-Disposition
  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);
};
